import { Card, BlockStack, InlineStack, Text, Box } from '@shopify/polaris';
import type { IconSource } from '@shopify/polaris';
import { IconBadge } from './IconBadge';
import type { IconBadgeColor } from './IconBadge';
import { Sparkline } from './Sparkline';

interface StatCardProps {
  label: string;
  value: string;
  icon?: IconSource;
  iconColor?: IconBadgeColor;
  /** Percent change vs. the previous period — omit when there's nothing real to compare against. */
  delta?: number;
  deltaLabel?: string;
  sparklineValues?: number[];
}

export function StatCard({ label, value, icon, iconColor = 'indigo', delta, deltaLabel, sparklineValues }: StatCardProps) {
  const hasDelta = typeof delta === 'number';
  const isUp = hasDelta && delta >= 0;
  const hasSparkline = !!sparklineValues && sparklineValues.length >= 2;
  const showFooterRow = hasDelta || !!deltaLabel || hasSparkline;

  return (
    <Card>
      <BlockStack gap={showFooterRow ? '300' : '100'}>
        <InlineStack align="space-between" blockAlign="start" wrap={false} gap="200">
          <BlockStack gap="150">
            <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
            <Text as="p" variant="heading2xl" fontWeight="bold">{value}</Text>
          </BlockStack>
          {icon && <IconBadge icon={icon} color={iconColor} />}
        </InlineStack>

        {showFooterRow && (
          <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
            {hasDelta ? (
              <span className={`gp-delta ${isUp ? 'gp-delta--up' : 'gp-delta--down'}`}>
                {isUp ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
              </span>
            ) : deltaLabel ? (
              <Text as="span" variant="bodySm" tone="subdued">{deltaLabel}</Text>
            ) : null}

            {hasSparkline && (
              <Box>
                <Sparkline values={sparklineValues} color={isUp ? '#16a34a' : '#4f46e5'} />
              </Box>
            )}
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}
